import type NumberUnit from '@exodus/currency';
import { VALIDATION_TYPES } from '@exodus/send-validation/src/constants';
import { useSelector } from 'react-redux';

import type { Asset, FeesResult } from '@/types';
import formatFiatSelector from '@/ui/flux/fiat/selectors/format-fiat';
import selectors from '@/ui/flux/selectors';
import useConversion from '@/ui/hooks/use-conversion';
import useReceiveAddress from '@/ui/hooks/use-receive-address';
import type { Nft } from '@/ui/types';
import { createValidationHookWithDefaults } from '@/ui/utils/validation';
import type { IsBuySupportedByAssetFn } from './use-is-buy-supported-by-asset-fn.js';
import useIsBuySupportedByAssetFn from './use-is-buy-supported-by-asset-fn.js';

type SendValidationDependencies = {
  fundsLoading?: boolean;
  asset: any;
  amount?: NumberUnit | null;
  availableBalance?: NumberUnit;
  sendAmount?: NumberUnit | null;
  isFeeAssetEnough: boolean;
  isBuySupportedByAsset: IsBuySupportedByAssetFn;
  destinationAddress: string;
  addressDetails: Record<string, never>;
  fees?: FeesResult;
  spendableBalance: NumberUnit;
  accountState: any;
  fromAddress?: string;
  getAction?: (params: any) => any;
  getFiatValue: (value: NumberUnit) => NumberUnit;
  formatPrice: (price: string | number) => string;
  nft?: Nft;
};

const customizations = {
  BASE_ASSET_INSUFFICIENT_FUNDS: {
    getAction: ({ asset, isBuySupportedByAsset }) => {
      if (isBuySupportedByAsset(asset.feeAsset.name).value) {
        return {
          label: `Buy ${asset.feeAsset.displayTicker}`,
          navigateTo: `/buy`,
          navigateToState: { token: asset?.feeAsset?.name },
        };
      }
      return undefined;
    },
  },
} as any;

const useValidation =
  createValidationHookWithDefaults<SendValidationDependencies>({
    customizations,
  });

// TODO: Fetch asset address details
const addressDetails = {};

type UseSendValidationParams = {
  asset: Asset;
  walletAccount: string;
  toAddress: string;
  amount?: NumberUnit;
  nft?: Nft;
  availableBalance?: NumberUnit;
  fees?: FeesResult;
  isFeeAssetEnough?: boolean;
  isBuySupportedByAsset?: boolean;
};

const useSendValidation = ({
  asset,
  walletAccount,
  toAddress,
  amount,
  availableBalance,
  fees,
  nft,
  isFeeAssetEnough = false,
}: UseSendValidationParams) => {
  const isBuySupportedByAsset = useIsBuySupportedByAssetFn();

  const spendableBalance = useSelector(selectors.balances.getSpendable)({
    assetName: asset.name,
    walletAccount,
  });

  const { address: fromAddress } = useReceiveAddress({
    assetName: asset.baseAsset.name,
    walletAccount,
  });

  const accountState = useSelector(
    selectors.accountStates.createBaseAssetSourceSelector({
      assetName: asset.baseAsset.name,
      walletAccount,
    }),
  );

  const getFiatValue = useConversion(asset.name);

  const formatPrice = useSelector(formatFiatSelector);

  const deps = {
    nft,
    asset,
    destinationAddress: toAddress,
    addressDetails,
    amount,
    sendAmount: amount,
    availableBalance,
    isFeeAssetEnough,
    isBuySupportedByAsset,
    fees,
    spendableBalance,
    accountState,
    fromAddress,
    getFiatValue,
    formatPrice,
  } satisfies SendValidationDependencies;

  const { failedValidation } = useValidation(deps);

  if (!failedValidation) {
    return {};
  }

  const action = failedValidation.getAction?.(deps);
  const message = failedValidation.message;
  if (failedValidation.type === VALIDATION_TYPES.WARN) {
    return { warning: message, action };
  }
  return { error: message, action };
};

export default useSendValidation;
